import { CopyId } from "@/components/copy-id";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  isStartingBalance,
  ledgerEntryLabel,
  ledgerRefLabel,
} from "@/lib/credit-ledger";
import { formatDateTime } from "@/lib/formatters";
import { queryKeys } from "@/lib/queryKeys";
import { cn } from "@/lib/utils";
import {
  type CreditLedgerEntry,
  creditsProvider,
} from "@/providers/credits.provider";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";

function LedgerRow({ entry }: { entry: CreditLedgerEntry }) {
  const starting = isStartingBalance(entry);
  const refLabel = ledgerRefLabel(entry);

  return (
    <li className="flex items-start justify-between gap-3 py-2.5">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-sm font-medium">{ledgerEntryLabel(entry)}</p>
        {entry.note && !starting && (
          <p className="text-xs break-words whitespace-pre-wrap">{entry.note}</p>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span className="tabular-nums">{formatDateTime(entry.created_at)}</span>
          {refLabel && (
            <span className="flex min-w-0 items-center gap-1 break-all">
              {refLabel}
              <CopyId id={entry.ref_id!} />
            </span>
          )}
          {entry.actor_user_id && (
            <span className="flex min-w-0 items-center gap-1 break-all">
              By
              <CopyId id={entry.actor_user_id} />
            </span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <span
          className={cn(
            "text-sm font-medium tabular-nums",
            starting
              ? "text-foreground"
              : entry.delta > 0
                ? "text-green-600"
                : entry.delta < 0
                  ? "text-destructive"
                  : "text-muted-foreground",
          )}
        >
          {entry.delta > 0 ? "+" : entry.delta < 0 ? "−" : ""}
          {Math.abs(entry.delta).toLocaleString()}
        </span>
        <span className="text-xs text-muted-foreground tabular-nums">
          = {entry.balance_after.toLocaleString()}
        </span>
      </div>
    </li>
  );
}

/** A user's credit history, newest first. Key it by user id to reset paging. */
export function CreditLedger({ userId }: { userId: string }) {
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, error, isPlaceholderData } = useQuery({
    queryKey: queryKeys.userCreditLedger(userId, page),
    queryFn: () => creditsProvider.getUserLedger(userId, page),
    placeholderData: keepPreviousData,
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-destructive">
        {(error as Error).message || "Could not load the credit ledger."}
      </p>
    );
  }

  if (!data?.data.length) {
    return (
      <p className="text-sm text-muted-foreground">
        {page > 1 ? "No more entries." : "No credit history yet."}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <ul
        className={cn(
          "divide-y rounded-lg border px-3",
          isPlaceholderData && "opacity-60",
        )}
      >
        {data.data.map((entry) => (
          <LedgerRow key={entry.credit_ledger_id} entry={entry} />
        ))}
      </ul>

      {(page > 1 || data.has_next) && (
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-9 gap-1 sm:h-8"
            disabled={page === 1 || isPlaceholderData}
            onClick={() => setPage((p) => p - 1)}
          >
            <IconChevronLeft className="size-3.5" />
            Newer
          </Button>
          <span className="text-sm text-muted-foreground">Page {page}</span>
          <Button
            variant="outline"
            size="sm"
            className="h-9 gap-1 sm:h-8"
            disabled={!data.has_next || isPlaceholderData}
            onClick={() => setPage((p) => p + 1)}
          >
            Older
            <IconChevronRight className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
